import React, { useEffect, useState } from 'react'
import { DatePicker as MuiDatePicker, DatePickerProps } from '@mui/x-date-pickers'
import { IconButton, TextField } from '@mui/material'
import moment, { Moment } from '../../Libs/moment'
import { makeStyles } from 'tss-react/mui'
import { B4Calendar, J1LeftarrowD, J1RightarrowD } from '@e3dc-react/icons'

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  buttonRoot: {
    padding: 0,
  },
  pointer: {
    cursor: 'pointer',
  },
}))

export interface ShellDatePickerProps extends Partial<DatePickerProps<Moment, Moment>> {
  defaultValue?: Moment
  handleDateChange: (date: Moment | null) => void
  maxFrom?: Moment
  minFrom?: Moment
  onNextClick?: () => void
  onPrevClick?: () => void
  disabled?: boolean
  fullWidth?: boolean
  pointerClass?: string
}

const DatePicker: React.VFC<ShellDatePickerProps> = ({
  defaultValue = moment(),
  handleDateChange,
  onNextClick,
  onPrevClick,
  value,
  maxDate,
  maxFrom,
  minDate,
  disabled,
  fullWidth,
  pointerClass,
  ...additionalProps
}) => {
  const { cx, classes } = useStyles()
  const reachedMax = maxFrom ? moment(value).isSameOrAfter(maxFrom) : false
  const reachedMin = minDate ? moment(value).isSameOrBefore(minDate) : false
  const [open, setOpen] = useState(false)
  const [dateToDisplay, setDateToDisplay] = useState<string>(moment(value ?? defaultValue).format(additionalProps.inputFormat))

  /**
   * select following date on right arrow click
   */
  const handleNextClick = (): void => {
    if (!value) return
    const nextDate = moment(value).clone()
    nextDate.add(1, 'days')
    handleDateChange(nextDate)
    if (onNextClick) {
      onNextClick()
    }
  }

  /**
   * select previous date on left arrow click
   */
  const handlePrevClick = (): void => {
    if (!value) return
    const prevDate = moment(value).clone()
    prevDate.subtract(1, 'days')
    handleDateChange(prevDate)
    if (onPrevClick) {
      onPrevClick()
    }
  }

  /**
   * Handle change of date value, allow passing of additional custom action
   */
  const handleChange = (date: Moment | null): void => {
    const formattedDate = moment(date, additionalProps.inputFormat)
    const isValid = moment(formattedDate, additionalProps.inputFormat, true).isValid()

    /**
     * Mui Datepicker returns a Moment in "normaltime" and "summertime" eg. when you pick a Date from August the Moment time will always be summertime.
     * This behavior results in a invinit loading when the correct time would be "normaltime".
     * To prevent this the date.local() part was added to always have the start of the day at 00:00
     */
    if (formattedDate && isValid) {
      formattedDate?.local()
      if (minDate && formattedDate.isBefore(minDate)) return handleDateChange(minDate)
      if (maxDate && formattedDate.isAfter(maxDate)) return handleDateChange(maxDate)
      handleDateChange(formattedDate)
    }
  }

  useEffect(() => {
    setDateToDisplay(moment(value ?? defaultValue).format(additionalProps.inputFormat))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalProps.inputFormat, value])

  //Todo: date format according to active locale
  return (
    <div className={classes.root}>
      <IconButton color="primary" onClick={handlePrevClick} classes={{ root: classes.buttonRoot }} disabled={reachedMin} size="large">
        <J1LeftarrowD fontSize="large" />
      </IconButton>
      <MuiDatePicker
        open={open}
        onClose={() => setOpen(false)}
        renderInput={props => {
          const isValid = moment(dateToDisplay, additionalProps.inputFormat, true).isValid()

          return (
            <TextField
              {...props}
              variant={'outlined'}
              disabled={disabled}
              className={cx(classes.pointer, pointerClass)}
              InputProps={{
                endAdornment: <B4Calendar className={classes.pointer} onClick={() => setOpen(true)} sx={{ mr: 1 }} />,
              }}
              error={!isValid}
              inputProps={{
                onChange: event => {
                  setDateToDisplay(event.currentTarget.value)
                  props.inputProps?.onChange?.(event)
                },
                value: dateToDisplay,
              }}
              fullWidth={fullWidth}
            />
          )
        }}
        DialogProps={{
          sx: theme => ({
            '& .MuiPickersToolbar-content': {
              [theme.breakpoints.down('sm')]: {
                marginLeft: 0,
              },
            },
          }),
        }}
        maxDate={maxDate}
        value={value ?? null}
        minDate={minDate}
        onChange={handleChange}
        {...additionalProps}
      />
      <IconButton color="primary" onClick={handleNextClick} classes={{ root: classes.buttonRoot }} disabled={reachedMax} size="large">
        <J1RightarrowD fontSize="large" />
      </IconButton>
    </div>
  )
}

export default DatePicker
